import { useCallback } from 'react';
import { useStore } from '../store';
import { send } from '../ws';
import { getWs } from '../wsConnection';
import { waitForAck } from '../utils/waitForAck';
import { correlationId } from "@interlucent/ids";
import type {
  ProfileMapApplicationReq,
  ProfileUnmapApplicationReq,
  ProfileUpdateMappingReq,
  ProfileBatchMapApplicationsReq,
  ProfileMapApplicationAck,
  ProfileBatchMapApplicationsAck,
  AckPayload
} from '@interlucent/organization-protocol';
import { mappingService } from '../services/mappingService';
import { logger } from '../log';

const log = logger('profile-mappings');

/**
 * Custom hook for profile-application mapping operations
 */
export function useProfileMappings(profileId: string, projectId: string) {
  const mappings = useStore(state => state.mappings);
  const applications = useStore(state => state.applications);

  // Get applications mapped to this profile
  const mappedApplications = mappingService.getMappedApplications(
    profileId,
    mappings,
    applications
  );

  // Get applications available to map (not already mapped)
  const availableApplications = mappingService.getAvailableApplications(
    profileId,
    projectId,
    mappings,
    applications
  );

  // Get mapping count
  const mappingCount = mappingService.getMappingCount(profileId, mappings);

  /**
   * Map an application to the profile
   */
  const addMapping = useCallback(async (
    appId: string,
    versionId?: string,
    settings?: Record<string, any>
  ): Promise<{ success: boolean; mappingId?: string; error?: string }> => {
    if (!getWs()) {
      return { success: false, error: 'WebSocket connection not available' };
    }

    // Check if already mapped
    if (mappingService.isMapped(profileId, appId, mappings)) {
      return {
        success: false,
        error: 'Application is already mapped to this profile'
      };
    }

    try {
      const corr = correlationId();
      const payload: ProfileMapApplicationReq = {
        profileId,
        appId,
        versionId,
        settings
      };

      send('mapping.map', payload, corr);

      const ack = await waitForAck<ProfileMapApplicationAck>(corr);

      if (ack.ok) {
        // If the mapping already existed on the server, we need to manually update the store
        // because no ProfileApplicationMapped event will be fired when existing: true
        if ((ack as any).existing) {
          const mappings = useStore.getState().mappings;

          // Add the existing mapping to the store if not already present
          if (!mappings[ack.mappingId]) {
            log.info(`adding existing mapping to store: ${ack.mappingId}`);
            useStore.setState({
              mappings: {
                ...mappings,
                [ack.mappingId]: {
                  id: ack.mappingId,
                  deploymentProfileId: profileId,
                  appId: appId,
                  createdAt: new Date().toISOString(),
                  updatedAt: new Date().toISOString()
                }
              }
            });
          }
        }

        return {
          success: true,
          mappingId: ack.mappingId
        };
      } else {
        return {
          success: false,
          error: 'Failed to map application'
        };
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to map application'
      };
    }
  }, [profileId, mappings]);

  /**
   * Remove an application mapping from the profile
   */
  const removeMapping = useCallback(async (
    appId: string
  ): Promise<{ success: boolean; error?: string }> => {
    if (!getWs()) {
      return { success: false, error: 'WebSocket connection not available' };
    }

    // Get the mapping
    const mapping = mappingService.getMapping(profileId, appId, mappings);
    if (!mapping) {
      return {
        success: false,
        error: 'Mapping not found'
      };
    }

    try {
      const corr = correlationId();
      const payload: ProfileUnmapApplicationReq = {
        profileId,
        appId,
        mappingId: mapping.id
      };

      send('mapping.unmap', payload, corr);

      const ack = await waitForAck<AckPayload>(corr);

      if (ack.ok) {
        return { success: true };
      } else {
        return {
          success: false,
          error: 'Failed to unmap application'
        };
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to unmap application'
      };
    }
  }, [profileId, mappings]);

  /**
   * Update an existing mapping (pin/unpin version, toggle preLaunch)
   */
  const updateMapping = useCallback(async (
    mappingId: string,
    updates: { versionId?: string | null; preLaunch?: boolean }
  ): Promise<{ success: boolean; error?: string }> => {
    if (!getWs()) {
      return { success: false, error: 'WebSocket connection not available' };
    }

    try {
      const corr = correlationId();
      const payload: ProfileUpdateMappingReq = {
        mappingId,
        ...updates,
      };

      send('mapping.update', payload, corr);

      const ack = await waitForAck<AckPayload>(corr);

      if (ack.ok) {
        return { success: true };
      } else {
        return { success: false, error: 'Failed to update mapping' };
      }
    } catch (error: any) {
      return { success: false, error: error.message || 'Failed to update mapping' };
    }
  }, []);

  /**
   * Batch map multiple applications
   */
  const batchAddMappings = useCallback(async (
    appIds: string[]
  ): Promise<{
    success: boolean;
    mappingIds?: string[];
    skipped?: string[];
    failed?: Array<{ appId: string; error: string }>;
    error?: string;
  }> => {
    if (!getWs()) {
      return { success: false, error: 'WebSocket connection not available' };
    }

    try {
      const corr = correlationId();
      const payload: ProfileBatchMapApplicationsReq = {
        profileId,
        applications: appIds.map(appId => ({ appId })),
        skipExisting: true
      };

      send('mapping.batchMap', payload, corr);

      const ack = await waitForAck<ProfileBatchMapApplicationsAck>(corr);

      if (ack.ok) {
        return {
          success: true,
          mappingIds: ack.mappingIds,
          skipped: ack.skipped,
          failed: ack.failed
        };
      } else {
        return {
          success: false,
          error: 'Failed to batch map applications'
        };
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to batch map applications'
      };
    }
  }, [profileId]);

  /**
   * Check if an application can be mapped
   */
  const canMapApplication = useCallback((appId: string) => {
    return mappingService.canMapApplication(profileId, appId, mappings, applications);
  }, [profileId, mappings, applications]);

  /**
   * Check if an application is mapped
   */
  const isMapped = useCallback((appId: string) => {
    return mappingService.isMapped(profileId, appId, mappings);
  }, [profileId, mappings]);

  return {
    mappedApplications,
    availableApplications,
    mappingCount,
    addMapping,
    removeMapping,
    updateMapping,
    batchAddMappings,
    canMapApplication,
    isMapped
  };
}
